import { nextSprint, prevSprint, sprintKeyOf } from '@nedelnik/shared';

import { Calendar, ChevronLeft, ChevronRight, Search, Umbrella } from '../icons.js';
import { InboxButton } from '../inbox/InboxButton.js';

import { carryableTasks, countByStatus, openCountIn, tasksInPeriod } from './counts.js';
import {
  currentLabel,
  isCurrentSprint,
  periodShort,
  periodTitle,
  periodSubtitle,
} from './labels.js';

import type { Period, SprintType, Task } from '@nedelnik/shared';

/**
 * Шапка: какой спринт открыт, что в нём и куда шагнуть.
 *
 * Переключателя «список / доска» здесь больше нет: формат выбирает не человек,
 * а ширина экрана (3.1.1).
 *
 * Счётчики у соседей — не украшение: спринты не заводятся и не заканчиваются
 * (3.2.2), поэтому единственный способ понять, есть ли смысл шагать вперёд, —
 * увидеть, сколько там незакрытых (3.2.4).
 */
export function SprintHeader({
  period,
  sprintType,
  tasks,
  now,
  searching,
  narrow,
  onPeriod,
  onCarryOver,
  onProjects,
  onSearch,
}: {
  period: Period;
  sprintType: SprintType;
  tasks: readonly Task[];
  now: Date;
  /** Открыта ли строка поиска: кнопка обязана показывать своё состояние. */
  searching: boolean;
  /**
   * Узкий ли экран. От этого зависит не оформление, а состав шапки: на телефоне
   * нет массового переноса (3.11.6). Прятать кнопку стилями нельзя — скрытая,
   * она остаётся в разметке и в обходе с клавиатуры.
   */
  narrow: boolean;
  onPeriod: (period: Period) => void;
  onCarryOver: () => void;
  onProjects: () => void;
  onSearch: () => void;
}): React.ReactElement {
  const inPeriod = tasksInPeriod(tasks, period);
  const counts = countByStatus(inPeriod);
  // Сколько уедет кнопкой «перенести» — без повторяющихся (3.19.3), поэтому
  // не то же самое, что сумма счётчиков «бэклог» и «в работе».
  const open = carryableTasks(inPeriod).length;

  const current = sprintKeyOf(now, sprintType);
  const inbox = openCountIn(tasks, 'inbox');

  // По краям — соседние спринты; из Входящих шагать некуда, там нет ни
  // предыдущего периода, ни следующего.
  const previous = period === 'inbox' ? undefined : prevSprint(period);
  const following = period === 'inbox' ? undefined : nextSprint(period);

  return (
    <header className="head">
      <div className="head__row">
        {previous !== undefined && (
          <NeighbourButton
            period={previous}
            count={openCountIn(tasks, previous)}
            direction={-1}
            onPeriod={onPeriod}
          />
        )}

        <div className="head__title">
          <h1>
            {periodTitle(period)}
            {isCurrentSprint(period, sprintType, now) && (
              <span className="chip">{currentLabel(period)}</span>
            )}
          </h1>
          <p className="head__sub">{periodSubtitle(period)}</p>
        </div>

        {following !== undefined && (
          <NeighbourButton
            period={following}
            count={openCountIn(tasks, following)}
            direction={1}
            onPeriod={onPeriod}
          />
        )}

        <div className="head__tools">
          {/*
            Поиск вызывается и убирается (3.12.1): он нужен изредка и не должен
            занимать основной экран, поэтому здесь стоит переключатель, а не
            всегда открытая строка.

            Имя у кнопки одно на оба состояния, меняется только `aria-pressed`.
            Второе имя («Убрать поиск») сделало бы на экране две одинаково
            названные кнопки — эту и крестик в самой строке, — а в озвучке они
            неразличимы. Ровно на этом обожглись с двумя «Закрыть» на этапе 11.
          */}
          <button
            type="button"
            className="inbox"
            aria-pressed={searching}
            aria-label="Искать задачи"
            onClick={onSearch}
          >
            <Search />
            <span className="tool__label">Поиск</span>
          </button>
          <button type="button" className="inbox" aria-label="Проекты" onClick={onProjects}>
            <Umbrella />
            <span className="tool__label">Проекты</span>
          </button>
          {/* На своём экране Входящие в себя не ведут — кнопки нет. */}
          {period !== 'inbox' && <InboxButton count={inbox} onOpen={() => onPeriod('inbox')} />}
          {period !== current && (
            /*
              Возврат к текущему спринту одинаков всюду: и во Входящих, где он
              единственный выход (соседних периодов там нет), и в чужой неделе,
              откуда иначе пришлось бы шагать стрелками. Раньше это были две
              разные кнопки — «К спринту н35» и «Сегодня», — и вторая на
              телефоне вовсе пряталась; владелец попросил вернуть её значком,
              как в первой.

              На узком экране от подписи остаётся номер спринта, полное имя
              живёт в `aria-label`.
            */
            <button
              type="button"
              className="inbox"
              aria-label={`К спринту ${periodShort(current)}`}
              onClick={() => onPeriod(current)}
            >
              <Calendar />
              <span className="tool__label">К спринту </span>
              {periodShort(current)}
            </button>
          )}
        </div>
      </div>

      <div className="head__stats">
        {/*
          На узком экране у счётчиков остаются точка и число: словами они
          занимали строку целиком, и кнопка массового переноса уезжала на
          следующую. Смысл цвета человек уже видел в заголовках групп списка
          («В РАБОТЕ», «БЭКЛОГ», «ГОТОВО» — те же точки), а в озвучке слово не
          теряется вовсе: оно в `aria-label`, который читается вместо
          содержимого.
        */}
        <span className="stat" role="img" aria-label={`Бэклог: ${String(counts.backlog)}`}>
          <i className="dot dot--backlog" aria-hidden="true" />
          <span className="stat__name">бэклог </span>
          <b>{counts.backlog}</b>
        </span>
        <span className="stat" role="img" aria-label={`В работе: ${String(counts.wip)}`}>
          <i className="dot dot--wip" aria-hidden="true" />
          <span className="stat__name">в работе </span>
          <b>{counts.wip}</b>
        </span>
        <span className="stat" role="img" aria-label={`Готово: ${String(counts.done)}`}>
          <i className="dot dot--done" aria-hidden="true" />
          <span className="stat__name">готово </span>
          <b>{counts.done}</b>
        </span>

        {/*
          Массовый перенос — способ закрыть неделю одним движением (3.11.4).
          Закрытые задачи остаются на месте: они принадлежат той неделе, когда
          были сделаны, и тащить их за собой значит переписывать историю.
        */}
        {/*
          Массового переноса на телефоне нет (3.11.6): вместо него задуман
          автоматический перенос незакрытых при наступлении нового спринта, а
          до тех пор это действие остаётся на большом экране.
        */}
        {!narrow && period !== 'inbox' && open > 0 && (
          /*
            Подпись была укорочена до «перенести (N)», чтобы кнопка вставала в
            одну строку со счётчиками. Но кнопка живёт только на большом
            экране, а там места хватает с запасом, и одно «перенести» не
            отвечало на вопрос «куда» (замечание владельца, 2026-09-13).
            «Спринт» остался в `aria-label`: на экране стрелка и соседство с
            счётчиками недели договаривают его сами.
          */
          <button
            type="button"
            className="stat stat--action"
            aria-label={`Перенести незакрытые задачи (${String(open)}) в следующий спринт`}
            onClick={onCarryOver}
          >
            <ChevronRight />
            Перенести в следующий ({open})
          </button>
        )}
      </div>
    </header>
  );
}

/**
 * Кнопка соседнего спринта.
 *
 * Во Входящих её нет вовсе, а не «есть, но не нажимается»: у Входящих нет ни
 * предыдущего периода, ни следующего, и бледная стрелка на этом месте читается
 * как сломанная кнопка (нашлось проверкой в браузере). Вернуться оттуда можно
 * кнопкой «К спринту …» — она обещает ровно то, что делает.
 *
 * Счётчик показывается только когда у соседа есть незакрытые: ноль в кружке
 * ничем не отличается от «не считали».
 */
function NeighbourButton({
  period,
  count,
  direction,
  onPeriod,
}: {
  period: Period;
  count: number;
  direction: -1 | 1;
  onPeriod: (period: Period) => void;
}): React.ReactElement {
  const label = direction < 0 ? 'Предыдущий спринт' : 'Следующий спринт';

  return (
    <button
      type="button"
      className="head__nav"
      aria-label={`${label}: ${periodShort(period)}`}
      title={periodShort(period)}
      onClick={() => onPeriod(period)}
    >
      {direction < 0 ? <ChevronLeft /> : <ChevronRight />}
      {count > 0 && <span className="count">{count}</span>}
    </button>
  );
}
